/**
 * ThirdfortReviewPanel - the Thirdfort report review, on a surface a fee
 * earner can actually reach.
 *
 * BM-H282. The fee earner approval shipped as BM-H274, but the only place
 * in the app that called POST /onboarding/{ref}/thirdfort-report-checked
 * was the panel that appears on the conflict search page immediately after
 * a clearance is saved. That panel renders solely off React state held for
 * the duration of that browser session, and there is no route or query
 * parameter that reopens it. So for any matter already on the pipeline the
 * approval had no surface at all: the pipeline tracker's "Review report"
 * button routed to the pre-CaseMinder registration page, which has no
 * Thirdfort control on it.
 *
 * This panel is the reachable surface. It is mounted on the matter page
 * (/matter/:matterRef) and drives the same three endpoints in the same
 * order, with the same rules:
 *
 *   Stage C  POST /onboarding/{ref}/thirdfort-complete
 *            the check itself has come back from Thirdfort
 *   Stage D  POST /onboarding/{ref}/thirdfort-report-received
 *            client care logs the report and notifies the fee earner
 *   Stage E  POST /onboarding/{ref}/thirdfort-report-checked
 *            the fee earner reads the report and approves, refers or
 *            rejects it. It is the APPROVAL that satisfies AML, never the
 *            receipt, and refusing is a first-class path.
 *
 * BM-H502, 11 September 2026. This panel used to `return null` whenever the
 * matter had no Thirdfort journey at all. That was the dead end: if the
 * automatic notification never stamped a status - because email sending was
 * switched off, or any other reason - the record stayed fully blank, and a
 * fully blank record made this panel disappear. The fee earner pressed
 * "Open AML / Thirdfort" on the pipeline tracker, landed here, and the whole
 * AML section was simply not on the page. Not an error, not an empty state -
 * gone, with no way forward in the app. Live case: BL.RM.001.1416, OB
 * complete for ten days with a blank thirdfort_status, held at the CDD+CMRA
 * gate.
 *
 * So the no-journey state is now a real state:
 *
 *   Stage B0 POST /onboarding/notify-thirdfort/{ref}
 *            "Start AML / Thirdfort check" - asks client care to send the
 *            client the identity-check link and records the journey, so the
 *            panel then shows the ordinary in-progress UI. The endpoint
 *            existed already and had never been called from anywhere in the
 *            front end; its path parameter resolves on matter_reference OR
 *            provisional_id, so the matter page's own matterRef is a valid
 *            key for it.
 *
 * The panel still renders nothing for a matter with no onboarding record at
 * all, which is a different thing from a record with nothing on it: there is
 * no AML step to start until a client has been onboarded.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    CheckCircle2, Loader2, ShieldCheck, ShieldAlert, AlertTriangle,
} from "lucide-react";
import { Button } from "./ui/button";
import { API } from "../config/api";

import { errorMessage } from "../utils/errorMessage";
import DirectorVouchRequest from "./DirectorVouchRequest"; // BM-H4024
// Thirdfort statuses that mean the check itself is finished.
const CHECK_DONE = ["complete", "approved", "clear", "pass", "overridden", "na"];

// BM-H502. Statuses that mean no journey has begun, so the matter needs the
// "start the check" control rather than the mid-journey controls. "" is a
// blank record; "not_notified" is the explicit status the backend now writes
// when it reached the notification but sending was switched off, which is
// deliberately distinct from "error" because nothing failed.
const NOT_STARTED = ["", "not_started", "not_notified"];

const OUTCOMES = [
    { value: "approved", label: "Approve", tone: "teal" },
    { value: "referred", label: "Refer", tone: "amber" },
    { value: "rejected", label: "Reject", tone: "red" },
];

export default function ThirdfortReviewPanel({
    matterRef, apiBase = API, onAmlSatisfied, onGoToForf,
}) {
    const [loading, setLoading] = useState(true);
    const [ob, setOb] = useState(null);
    const [busy, setBusy] = useState(null);
    const [affirmed, setAffirmed] = useState(false);
    const [notes, setNotes] = useState("");
    const [outcome, setOutcome] = useState("approved");
    // BM-H282: a matter can carry more than one verified natural person.
    // AML attaches to the person, so each individual gets their own report,
    // their own reading and their own decision.
    const [subjectName, setSubjectName] = useState("");
    const [addingAnother, setAddingAnother] = useState(false);
    // BM-H286: the reports already filed in this matter's own SharePoint
    // folder. A fee earner should be able to approve against a document the
    // firm already holds rather than downloading it and uploading it back.
    const [filedReports, setFiledReports] = useState(null);
    const [filedReason, setFiledReason] = useState(null);
    const [filedBusy, setFiledBusy] = useState(false);
    const [selectedFiled, setSelectedFiled] = useState("");
    // BM-H467. ``filedReports`` is now the shortlist that plausibly is
    // identity evidence; ``allFiledReports`` is every PDF in the folder,
    // one click away, because the firm's naming has drifted and a report
    // named something odd must still be reachable.
    const [allFiledReports, setAllFiledReports] = useState(null);
    const [showAllFiled, setShowAllFiled] = useState(false);
    const [subjectsKnown, setSubjectsKnown] = useState([]);
    // BM-H533. The preview used to point the iframe's ``src`` straight at
    // the protected backend endpoint. An <iframe src> is a bare browser
    // subrequest -- it never goes through axios, so it never reaches the
    // global response interceptor in ../config/api.js that catches a 401
    // SESSION_EXPIRED and redirects to /login. An expired session rendered
    // the backend's raw JSON error body verbatim inside the frame. Fixed
    // by mirroring DocumentPreviewDrawer.jsx's pattern: fetch the preview
    // via axios first (interceptor + errorMessage() apply normally, same
    // as every other authenticated read in the app), and only feed the
    // iframe a URL once that fetch has actually succeeded.
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const load = useCallback(async () => {
        if (!matterRef) return;
        try {
            const res = await axios.get(
                `${apiBase}/onboarding/status/${encodeURIComponent(matterRef)}`,
            );
            setOb(res.data || null);
        } catch (e) {
            // A matter with no onboarding record is not an error worth
            // shouting about; the panel simply does not apply.
            setOb(null);
        } finally {
            setLoading(false);
        }
    }, [matterRef, apiBase]);

    // BM-H286. Read only. Asks the backend what is actually sitting in this
    // matter's folder, walked to the bottom of the tree, and shows the human
    // the named files it found. "Could not read the folder" is shown as that
    // and never as "nothing is filed", because those are different answers.
    const loadFiledReports = useCallback(async () => {
        if (!matterRef) return;
        setFiledBusy(true);
        try {
            const res = await axios.get(
                `${apiBase}/onboarding/${encodeURIComponent(matterRef)}/filed-reports`,
            );
            const data = res.data || {};
            if (data.available) {
                const all = data.candidates || [];
                const shortlist = data.report_candidates || [];
                setAllFiledReports(all);
                setFiledReports(shortlist.length ? shortlist : all);
                setShowAllFiled(!shortlist.length);
                setSubjectsKnown(data.subjects_known || []);
                setFiledReason(
                    (data.read_errors || []).length
                        ? `Some folders could not be read (${data.read_errors.length}), so this list may be incomplete.`
                        : null,
                );
            } else {
                setFiledReports([]);
                setAllFiledReports([]);
                setFiledReason(
                    `Could not read this matter's SharePoint folder (${data.reason || "unknown"}). `
                    + "This is not the same as nothing being filed.",
                );
            }
        } catch (e) {
            setFiledReports([]);
            setFiledReason(
                "Could not read this matter's SharePoint folder. This is not "
                + "the same as nothing being filed.",
            );
        } finally {
            setFiledBusy(false);
        }
    }, [matterRef, apiBase]);

    useEffect(() => { load(); }, [load]);

    // BM-H533. Fetch the selected filed report's bytes via axios (blob),
    // so the global interceptor and errorMessage() handling apply exactly
    // as they do for every other authenticated read, then hand the iframe
    // an object URL only once that fetch has actually succeeded. Revokes
    // the previous object URL on every change and on unmount, so a fee
    // earner clicking through several candidates never leaks blob URLs.
    useEffect(() => {
        if (!selectedFiled || !matterRef) {
            setPreviewUrl(null);
            setPreviewError(null);
            return undefined;
        }
        let cancelled = false;
        let objectUrl = null;
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewUrl(null);
        axios.get(
            `${apiBase}/onboarding/${encodeURIComponent(matterRef)}/filed-reports/${encodeURIComponent(selectedFiled)}/preview`,
            { responseType: "blob" },
        ).then((res) => {
            if (cancelled) return;
            objectUrl = URL.createObjectURL(res.data);
            setPreviewUrl(objectUrl);
        }).catch((e) => {
            if (cancelled) return;
            // A 401 SESSION_EXPIRED is handled by the global interceptor
            // (redirect to /login) before this ever runs; any other
            // failure gets the app's normal errorMessage()-friendly text
            // instead of a raw response dumped into the frame.
            setPreviewError(errorMessage(e, "Could not load the report preview."));
        }).finally(() => {
            if (!cancelled) setPreviewLoading(false);
        });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [selectedFiled, matterRef, apiBase]);

    // A matter with no onboarding record at all has no AML step to start yet.
    if (loading || !ob) return null;

    const status = (ob.thirdfort_status || "").toLowerCase();
    const receivedAt = ob.thirdfort_report_received_at;
    const checkedAt = ob.thirdfort_report_checked_at;
    const reportOutcome = ob.thirdfort_report_outcome;
    const inheritedFrom = ob.aml_inherited_from_matter_reference;
    // BM-H579. The AML on this matter is approved but cannot be attributed to
    // an individual this matter's own onboarding identifies, so it will refuse
    // to carry to a linked matter. Derived by the backend on every read, which
    // is why it shows on matters approved long before this shipped. It stays
    // on the panel until somebody corrects the record and re-approves; it is
    // not a dismissible toast, because the last one was missed for days.
    const subjectWarning = ob.aml_subject_warning;
    const reviewedSubjects = Array.isArray(ob.thirdfort_report_subjects)
        ? ob.thirdfort_report_subjects.filter(Boolean)
        : [];

    const notifiedAt = ob.thirdfort_notified_at;
    const statusReason = ob.thirdfort_status_reason;

    // BM-H502. No Thirdfort journey on this matter, and no inherited one
    // either. This used to `return null`. Now it is a state with a control.
    const journeyStarted = Boolean(
        (status && !NOT_STARTED.includes(status))
        || receivedAt || checkedAt || inheritedFrom || notifiedAt,
    );

    const checkDone = CHECK_DONE.includes(status);
    const approved = reportOutcome === "approved" || reportOutcome === "no_concerns";

    const post = async (key, fn) => {
        setBusy(key);
        try {
            await fn();
            await load();
        } finally {
            setBusy(null);
        }
    };

    // BM-H502 Stage B0. Start the journey. Wires up the endpoint that has
    // been sitting in the backend uncalled, keyed on this page's matterRef.
    const startCheck = () => post("start", async () => {
        try {
            const res = await axios.post(
                `${apiBase}/onboarding/notify-thirdfort/${encodeURIComponent(matterRef)}`,
                {},
            );
            const data = res.data || {};
            if (data.email_sent) {
                toast.success(
                    "Client care have been asked to send the client their "
                    + "Thirdfort link. The AML check is now in progress.",
                );
            } else {
                // Honest: the step is now recorded and visible, but no email
                // left the building, so say so rather than claiming success.
                toast.warning(
                    data.message
                    || "The AML step is now recorded on this matter, but no "
                    + "notification could be sent. Ask client care to send "
                    + "the client their Thirdfort link.",
                );
            }
        } catch (e) {
            toast.error(
                errorMessage(e, "Could not start the AML / Thirdfort check on this matter."),
            );
        }
    });

    if (!journeyStarted) {
        return (
            <section
                id="thirdfort-review"
                className="mb-5 p-4 bg-[rgba(26,37,71,0.7)] rounded-md border border-navy-border"
                data-testid="thirdfort-review-panel"
            >
                <div className="flex items-center gap-2 mb-3">
                    <ShieldAlert className="w-4 h-4 text-status-amber" />
                    <h3 className="text-sm font-semibold text-white">
                        AML / Thirdfort check not started
                    </h3>
                    <span
                        className="ml-auto text-xs text-text-muted"
                        data-testid="thirdfort-status-chip"
                    >
                        check: {status || "not started"}
                    </span>
                </div>

                <p className="text-xs text-text-muted mb-2" data-testid="thirdfort-not-started-note">
                    No Thirdfort identity check has been requested for this
                    matter yet, so there is nothing to review. Starting it asks
                    client care to send the client their Thirdfort link. AML has
                    to be approved before money on account can be taken or the
                    matter can pass the CDD stage.
                </p>

                {statusReason && (
                    <p
                        className="text-xs text-amber-300 mb-2"
                        data-testid="thirdfort-not-started-reason"
                    >
                        {statusReason}
                    </p>
                )}

                <Button
                    size="sm"
                    onClick={startCheck}
                    disabled={busy === "start"}
                    className="bg-teal hover:bg-teal text-xs"
                    data-testid="thirdfort-start-check-btn"
                >
                    {busy === "start" && (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    )}
                    Start AML / Thirdfort check
                </Button>
                {/* BM-H4024: hand the matter to a director to vouch for ID. */}
                <DirectorVouchRequest matterRef={matterRef} ob={ob} apiBase={apiBase} onSent={load} />
            </section>
        );
    }

    const markCheckComplete = () => post("complete", async () => {
        try {
            await axios.post(
                `${apiBase}/onboarding/${encodeURIComponent(matterRef)}/thirdfort-complete`,
                {},
            );
            toast.success("Thirdfort check marked complete.");
        } catch (e) {
            toast.error(
                errorMessage(e, "Could not mark the Thirdfort check complete."),
            );
        }
    });

    const markReceived = () => post("received", async () => {
        try {
            const res = await axios.post(
                `${apiBase}/onboarding/${encodeURIComponent(matterRef)}/thirdfort-report-received`,
                {},
            );
            if (res.data?.already_recorded) {
                toast.info("The report was already logged as received.");
            } else if (res.data?.notify_status === "logged_fee_earner_unresolved") {
                toast.warning(
                    "Logged as received, but the fee earner for this matter "
                    + "could not be identified, so the notification went to "
                    + "the shared mailbox.",
                );
            } else {
                toast.success("Report logged as received. Fee earner notified.");
            }
        } catch (e) {
            toast.error(
                errorMessage(e, "Could not log the Thirdfort report as received."),
            );
        }
    });

    const submitReview = (chosen) => {
        const isApproval = chosen === "approved";
        // A further individual needs their OWN report on file; the artefact
        // already stored belongs to somebody else.
        if (!selectedFiled && (!checkedAt || addingAnother)) {
            toast.error(
                "Choose the Thirdfort report you reviewed from the ones filed "
                + "on this matter.",
            );
            return;
        }
        if (addingAnother && !subjectName.trim()) {
            toast.error("Name the individual this report verifies.");
            return;
        }
        if (isApproval && !affirmed) {
            toast.error(
                "Confirm you have read the Thirdfort report before approving.",
            );
            return;
        }
        if (!isApproval && !notes.trim()) {
            toast.error("Give a reason when you do not approve the report.");
            return;
        }
        return post("review", async () => {
            try {
                const form = new FormData();
                if (selectedFiled) {
                    form.append("filed_report_item_id", selectedFiled);
                }
                form.append("outcome", chosen);
                form.append("affirm_read", isApproval ? "true" : "false");
                if (notes.trim()) form.append("notes", notes.trim());
                if (subjectName.trim()) {
                    form.append("subject_name", subjectName.trim());
                }
                const res = await axios.post(
                    `${apiBase}/onboarding/${encodeURIComponent(matterRef)}/thirdfort-report-checked`,
                    form,
                );
                setSelectedFiled("");
                setAffirmed(false);
                setSubjectName("");
                setAddingAnother(false);
                if (res.data?.already_recorded) {
                    toast.info("This report has already been approved.");
                } else if (res.data?.aml_satisfied) {
                    // BM-H670: the next step is the FORF. Say so, with the
                    // button, instead of leaving the fee earner to find it on
                    // the pipeline board; and refresh the matter page's gate
                    // so its own Go to FORF card appears without a reload.
                    toast.success(
                        res.data.pipeline_stage
                            ? `Report approved. AML satisfied, matter now at ${res.data.pipeline_stage}.`
                            : "Report approved. AML satisfied.",
                        onGoToForf
                            ? {
                                action: {
                                    label: "Go to FORF & Risk Assessment",
                                    onClick: onGoToForf,
                                },
                                duration: 15000,
                            }
                            : undefined,
                    );
                    if (onAmlSatisfied) onAmlSatisfied();
                    // BM-H579. Said at the moment it is created, as well as
                    // on the panel, because this is the state that quietly
                    // refuses a linked matter's inheritance weeks later.
                    if (res.data.aml_subject_warning) {
                        toast.warning(
                            res.data.aml_subject_warning.headline,
                            {
                                description: res.data.aml_subject_warning.detail,
                                duration: 15000,
                            },
                        );
                    }
                } else {
                    toast.warning(
                        `Report not approved (${res.data?.thirdfort_report_outcome}). `
                        + "AML stays outstanding and the matter is held at the "
                        + "AML check.",
                    );
                }
            } catch (e) {
                toast.error(
                    errorMessage(e, "Could not record the Thirdfort report review."),
                );
            }
        });
    };

    return (
        <section
            id="thirdfort-review"
            className="mb-5 p-4 bg-[rgba(26,37,71,0.7)] rounded-md border border-navy-border"
            data-testid="thirdfort-review-panel"
        >
            <div className="flex items-center gap-2 mb-3">
                {approved
                    ? <ShieldCheck className="w-4 h-4 text-teal" />
                    : <ShieldAlert className="w-4 h-4 text-status-amber" />}
                <h3 className="text-sm font-semibold text-white">
                    AML / Thirdfort report review
                </h3>
                {status && (
                    <span
                        className="ml-auto text-xs text-text-muted"
                        data-testid="thirdfort-status-chip"
                    >
                        check: {status}
                    </span>
                )}
            </div>

            {inheritedFrom && (
                <p className="mb-3 text-xs text-text-muted" data-testid="thirdfort-inherited-note">
                    AML on this matter is inherited from {inheritedFrom}
                    {ob.aml_inherited_stale
                        ? ". The inherited result is flagged for re-verification."
                        : "."}
                </p>
            )}

            {subjectWarning && (
                <div
                    className="mb-3 p-3 rounded-md border border-amber-500/60 bg-amber-500/10"
                    data-testid="aml-subject-warning"
                >
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                        <div className="text-xs leading-relaxed">
                            <p className="font-semibold text-amber-200">
                                {subjectWarning.headline}
                            </p>
                            <p className="mt-1 text-amber-100/90">
                                {subjectWarning.detail}
                            </p>
                            {(subjectWarning.candidates || []).length > 0 && (
                                <p
                                    className="mt-1 text-amber-100/80"
                                    data-testid="aml-subject-warning-candidates"
                                >
                                    People this matter identifies:{" "}
                                    {subjectWarning.candidates.join(", ")}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Stage C - the check itself */}
            {!checkDone && !receivedAt && (
                <div className="mb-3" data-testid="thirdfort-stage-c">
                    <p className="text-xs text-text-muted mb-2">
                        The Thirdfort check has not come back yet. Mark it
                        complete once the client has finished it.
                    </p>
                    <Button
                        size="sm"
                        onClick={markCheckComplete}
                        disabled={busy === "complete"}
                        className="bg-teal hover:bg-teal text-xs"
                        data-testid="thirdfort-mark-complete-btn"
                    >
                        {busy === "complete" && (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        )}
                        Mark Thirdfort check complete
                    </Button>
                </div>
            )}

            {/* Stage D - receipt */}
            {checkDone && !receivedAt && (
                <div className="mb-3" data-testid="thirdfort-stage-d">
                    <p className="text-xs text-text-muted mb-2">
                        Log the report as received. This notifies the fee
                        earner that there is a report to read.
                    </p>
                    <Button
                        size="sm"
                        onClick={markReceived}
                        disabled={busy === "received"}
                        className="bg-teal hover:bg-teal text-xs"
                        data-testid="thirdfort-mark-received-btn"
                    >
                        {busy === "received" && (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        )}
                        Mark report received
                    </Button>
                </div>
            )}

            {/* Stage E - the fee earner review */}
            {receivedAt && (!checkedAt || addingAnother) && (
                <div className="space-y-3" data-testid="thirdfort-stage-e">
                    <p className="text-xs text-text-muted">
                        Read the report, then record your decision. The
                        approval is what satisfies AML.
                    </p>

                    <input
                        type="text"
                        list="thirdfort-known-subjects"
                        value={subjectName}
                        onChange={(e) => setSubjectName(e.target.value)}
                        placeholder={
                            subjectsKnown.length
                                ? `Who this report verifies, e.g. ${subjectsKnown[0]}`
                                : "Name of the individual this report verifies"
                        }
                        className="w-full text-xs bg-[rgba(13,20,36,0.8)] border border-navy-border rounded p-2 text-white"
                        data-testid="thirdfort-subject-name-input"
                    />
                    <datalist id="thirdfort-known-subjects">
                        {subjectsKnown.map((n) => (
                            <option key={n} value={n} />
                        ))}
                    </datalist>
                    {subjectsKnown.length > 0 && !subjectName && (
                        <div className="flex flex-wrap gap-1" data-testid="thirdfort-subject-suggestions">
                            {subjectsKnown.map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setSubjectName(n)}
                                    className="text-xs underline text-white/80"
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="rounded border border-white/15 p-2 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-white/80">
                                Reports already filed on this matter
                            </span>
                            <button
                                type="button"
                                className="text-xs underline text-white/80"
                                onClick={loadFiledReports}
                                disabled={filedBusy}
                                data-testid="thirdfort-find-filed-reports"
                            >
                                {filedBusy ? "Looking..." : "Look in SharePoint"}
                            </button>
                        </div>
                        {filedReason && (
                            <p className="text-xs text-amber-300" data-testid="thirdfort-filed-reports-reason">
                                {filedReason}
                            </p>
                        )}
                        {filedReports && filedReports.length === 0 && !filedReason && (
                            <p className="text-xs text-white/60">
                                Walked this matter&apos;s folders and found no PDF
                                filed. File the report in this matter&apos;s
                                SharePoint folder, then look again.
                            </p>
                        )}
                        {filedReports && filedReports.length > 0 && (
                            <ul
                                className="space-y-1 max-h-56 overflow-y-auto pr-1"
                                data-testid="thirdfort-filed-reports-list"
                            >
                                {filedReports.map((r) => (
                                    <li key={r.item_id}>
                                        <label className="flex items-start gap-2 text-xs text-white cursor-pointer">
                                            <input
                                                type="radio"
                                                name="filed-report"
                                                className="mt-0.5"
                                                checked={selectedFiled === r.item_id}
                                                onChange={() => setSelectedFiled(r.item_id)}
                                            />
                                            <span>
                                                <span className="font-medium">{r.name}</span>
                                                <span className="block text-white/50">
                                                    {r.path}
                                                    {typeof r.size === "number"
                                                        ? ` (${r.size.toLocaleString()} bytes)`
                                                        : ""}
                                                </span>
                                                {r.subject_hint && (
                                                    <span className="block text-white/50">
                                                        Filename names {r.subject_hint}. Check it
                                                        yourself before approving.
                                                    </span>
                                                )}
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {allFiledReports && allFiledReports.length > (filedReports || []).length && (
                            <button
                                type="button"
                                className="text-xs underline text-white/60"
                                onClick={() => {
                                    setShowAllFiled(!showAllFiled);
                                    setFiledReports(
                                        showAllFiled
                                            ? allFiledReports.filter((c) => c.is_likely_report)
                                            : allFiledReports,
                                    );
                                }}
                                data-testid="thirdfort-toggle-all-filed"
                            >
                                {showAllFiled
                                    ? "Show only likely reports"
                                    : `Show all ${allFiledReports.length} documents in this matter`}
                            </button>
                        )}

                        {/* BM-H467. Read the report here. The tick below
                            certifies that it has been read, so the document
                            has to be visible in the same place.

                            BM-H533. The frame is only ever given a URL once
                            the axios blob fetch above has actually
                            succeeded (an object URL for the PDF bytes), so
                            an expired session or any other failure is
                            caught by the same interceptor + errorMessage()
                            path every other authenticated read uses,
                            never rendered raw inside the frame. */}
                        {selectedFiled && (
                            <div
                                className="rounded border border-navy-border overflow-hidden bg-slate-100"
                                data-testid="thirdfort-report-preview"
                            >
                                {previewLoading && (
                                    <div
                                        className="w-full h-[520px] flex items-center justify-center text-slate-500 text-xs gap-2"
                                        data-testid="thirdfort-report-preview-loading"
                                    >
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Loading preview...
                                    </div>
                                )}
                                {!previewLoading && previewError && (
                                    <div
                                        className="w-full h-[520px] flex flex-col items-center justify-center gap-2 text-slate-600 text-xs px-6 text-center"
                                        data-testid="thirdfort-report-preview-error"
                                    >
                                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                                        {previewError}
                                    </div>
                                )}
                                {!previewLoading && !previewError && previewUrl && (
                                    <iframe
                                        title="Thirdfort report"
                                        src={previewUrl}
                                        className="w-full h-[520px]"
                                        style={{ border: "none", overflow: "auto" }}
                                        data-testid="thirdfort-report-preview-iframe"
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    <label className="flex items-start gap-2 text-xs text-white cursor-pointer">
                        <input
                            type="checkbox"
                            checked={affirmed}
                            onChange={(e) => setAffirmed(e.target.checked)}
                            className="mt-0.5"
                            data-testid="thirdfort-affirm-read-checkbox"
                        />
                        <span>
                            I have read the Thirdfort report for this matter
                            and I am satisfied with it.
                        </span>
                    </label>

                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        placeholder="Notes. Required if you refer or reject."
                        className="w-full text-xs bg-[rgba(13,20,36,0.8)] border border-navy-border rounded p-2 text-white"
                        data-testid="thirdfort-review-notes-input"
                    />

                    <div className="flex flex-wrap gap-2">
                        {OUTCOMES.map((o) => (
                            <Button
                                key={o.value}
                                size="sm"
                                variant={o.value === "approved" ? "default" : "outline"}
                                onClick={() => { setOutcome(o.value); submitReview(o.value); }}
                                disabled={busy === "review"}
                                className="text-xs"
                                data-testid={`thirdfort-review-${o.value}-btn`}
                            >
                                {busy === "review" && outcome === o.value && (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                )}
                                {o.label}
                            </Button>
                        ))}
                    </div>
                </div>
            )}

            {checkedAt && (
                <div
                    className="flex items-start gap-2 text-xs text-teal"
                    data-testid="thirdfort-review-recorded"
                >
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    <div>
                        <p className="font-semibold">
                            Report {reportOutcome || "reviewed"}
                        </p>
                        <p className="text-teal/80">
                            {ob.thirdfort_report_reviewed_by || ob.thirdfort_report_checked_by || "fee earner"}
                            {" on "}
                            {String(checkedAt).slice(0, 10)}
                            {ob.thirdfort_report_review_notes
                                ? ` - ${ob.thirdfort_report_review_notes}`
                                : ""}
                        </p>
                        {reviewedSubjects.length > 0 && (
                            <p
                                className="text-teal/80"
                                data-testid="thirdfort-reviewed-subjects"
                            >
                                Individuals verified: {reviewedSubjects.join(", ")}
                            </p>
                        )}
                        {!addingAnother && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setAddingAnother(true)}
                                className="mt-2 text-xs"
                                data-testid="thirdfort-review-another-person-btn"
                            >
                                Review another individual
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* BM-H4024: hand the matter to a director to vouch for ID. */}
            <DirectorVouchRequest matterRef={matterRef} ob={ob} apiBase={apiBase} onSent={load} />
        </section>
    );
}
